import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAudio } from '@/contexts/AudioContext';
import {
  Crown, GraduationCap, Users, Radio, Sparkles, Archive as ArchiveIcon,
  LogOut, Shield, Home, Loader2, VolumeX, Sprout, Heart, Atom,
  ChevronRight, Send, Globe, HelpCircle, BookOpen, ShieldCheck,
} from 'lucide-react';
import { PlatinumComingSoon } from '@/pages/platinum/PlatinumComingSoon';
import { PlatinumAdmin } from '@/pages/platinum/PlatinumAdmin';
import { PlatinumTraining } from '@/pages/platinum/PlatinumTraining';
import NotificationBell from '@/components/NotificationBell';
import ProfilePicture from '@/components/ProfilePicture';
import { ElevenLogo } from '@/components/platinum/ElevenLogo';
import platinumBg from '@/assets/platinum-bg.jpg';

const STATEMENT = 'We participate with clarity, integrity, and collective intention. We contribute consciously. We co\u2011create responsibly. We honor the field and each person as the I AM.';

const NAV = [
  { key: 'home',        label: 'Sanctuary',                          icon: Home },
  { key: 'training',    label: 'Training',                           icon: GraduationCap },
  { key: 'community',   label: 'Community Engagement',               icon: Users },
  { key: 'livestreams', label: 'Live Streams & Activations',         icon: Radio },
  { key: 'cocreation',  label: 'Co\u2011Creation Chamber',           icon: Sparkles },
  { key: 'archive',     label: 'Archive of Collective Work',         icon: ArchiveIcon },
];

const SECTION_META = {
  community:   { title: 'Community Engagement', desc: 'Chat, intention pods & circles, focus-area submittals, and Collective Prioritization.' },
  livestreams: { title: 'Live Streams & Activations', desc: 'Gather live for guided activations and collective sessions.' },
  cocreation:  { title: 'Co\u2011Creation Chamber', desc: 'Collective Creation Boards — collaborate on new intentions, teachings, and manifestations.' },
  archive:     { title: 'Archive of Collective Work', desc: 'A living record of the intentions this collective has set into motion.' },
};

const PlatinumPlus = ({ user }) => {
  const navigate = useNavigate();
  const { stopMusic } = useAudio();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [isPlatAdmin, setIsPlatAdmin] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [view, setView] = useState('home');

  // intro gate
  const [introUrl, setIntroUrl] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  const [introMuted, setIntroMuted] = useState(false);
  const introRef = useRef(null);

  useEffect(() => { stopMusic(); }, [stopMusic]);

  useEffect(() => {
    (async () => {
      try {
        const a = await axios.get(`${API}/platinum-plus/access`);
        setAccess(a.data.access);
        setIsPlatAdmin(a.data.is_platinum_admin);
        setIsPlatformAdmin(a.data.is_platform_admin);
        if (a.data.access) {
          const intro = await axios.get(`${API}/platinum-plus/intro`);
          if (intro.data.should_play && intro.data.video_url) {
            setIntroUrl(intro.data.video_url);
            setShowIntro(true);
          }
        }
      } catch (e) {
        setAccess(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!showIntro || !introUrl) return;
    const v = introRef.current;
    if (!v) return;
    v.muted = false;
    const p = v.play();
    if (p && p.catch) p.catch(() => { v.muted = true; setIntroMuted(true); v.play().catch(() => {}); });
  }, [showIntro, introUrl]);

  const finishIntro = useCallback(() => {
    axios.post(`${API}/platinum-plus/intro-seen`).catch(() => {});
    setShowIntro(false);
  }, []);

  const goMain = () => navigate('/dashboard/portal');

  if (loading) {
    return (
      <div className="pp-root pp-center">
        <style>{THEME_CSS}</style>
        <div className="pp-bg" /><div className="pp-veil" />
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7F0D6C', position: 'relative', zIndex: 2 }} />
      </div>
    );
  }

  if (!access) {
    return (
      <div className="pp-root pp-center">
        <style>{THEME_CSS}</style>
        <div className="pp-bg" /><div className="pp-veil" />
        <div className="pp-locked" data-testid="platinum-no-access">
          <Crown className="w-10 h-10 mx-auto mb-3" style={{ color: '#7F0D6C' }} />
          <h2>Platinum+ awaits</h2>
          <p>This sanctuary opens to Platinum members once you complete the section “Let Your Vibration Be Your Calling Card.”</p>
          <button className="pp-btn-solid" onClick={goMain} data-testid="platinum-return-locked">Return to Main Platform</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-root" data-testid="platinum-plus-app">
      <style>{THEME_CSS}</style>
      <div className="pp-bg" style={{ backgroundImage: `url(${platinumBg})` }} />
      <div className="pp-veil" />

      {/* Forced one-time intro video */}
      {showIntro && introUrl && (
        <div className="pp-intro" data-testid="platinum-intro-overlay">
          <video
            ref={introRef}
            src={`${process.env.REACT_APP_BACKEND_URL}${introUrl}`}
            autoPlay
            playsInline
            onEnded={finishIntro}
            data-testid="platinum-intro-video"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
          {introMuted && (
            <button className="pp-unmute" data-testid="platinum-intro-unmute"
              onClick={() => { const v = introRef.current; if (v) { v.muted = false; setIntroMuted(false); } }}>
              <VolumeX className="w-4 h-4" /> Tap for sound
            </button>
          )}
        </div>
      )}

      {/* Top ribbon (Aubergine + warm light-bloom) */}
      <header className="pp-topbar" data-testid="platinum-topbar">
        <div className="pp-topbar-brand">
          <span className="pp-topbar-badge"><ElevenLogo mark className="pp-topbar-logo" /></span>
          <div className="pp-topbar-titles">
            <span className="pp-topbar-word">Collective Connection</span>
            <span className="pp-topbar-sub">We Co‑Create Consciously.</span>
          </div>
        </div>
        <div className="pp-topbar-actions">
          <span className="pp-member-badge" data-testid="platinum-member-badge">
            <Crown className="w-4 h-4" /> Platinum · Member
          </span>
          <div className="pp-bell" data-testid="platinum-notifications">
            <NotificationBell />
          </div>
          {user?.id && (
            <button className="pp-user-trigger" onClick={() => navigate('/dashboard/settings')}
              data-testid="platinum-user-settings" aria-label="Open settings">
              <ProfilePicture src={user.profile_picture} alt={user.full_name} size="sm" />
            </button>
          )}
        </div>
      </header>

      <div className="pp-body">
        {/* Left navigation (Cream) — hidden on the Sanctuary landing since the blocks are the nav */}
        {view !== 'home' && (
        <aside className="pp-ribbon" data-testid="platinum-ribbon">
          <div className="pp-brand">
            <span className="pp-crown"><Crown className="w-5 h-5" /><span className="pp-plus">+</span></span>
            <span>Platinum+</span>
          </div>
          <nav className="pp-nav">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <button key={n.key}
                  className={`pp-nav-item ${view === n.key ? 'active' : ''}`}
                  onClick={() => setView(n.key)}
                  data-testid={`platinum-nav-${n.key}`}>
                  <Icon className="w-4 h-4" /> <span>{n.label}</span>
                </button>
              );
            })}
            {isPlatAdmin && (
              <button className={`pp-nav-item ${view === 'admin' ? 'active' : ''}`} onClick={() => setView('admin')} data-testid="platinum-nav-admin">
                <Shield className="w-4 h-4" /> <span>Admin Area</span>
              </button>
            )}
          </nav>
          <button className="pp-return" onClick={goMain} data-testid="platinum-return-main">
            <LogOut className="w-4 h-4" /> <span>Return to Main Platform</span>
          </button>
        </aside>
        )}

        {/* Content */}
        <main className="pp-content">
          {view === 'home' && (
            <PlatinumHome
              statement={STATEMENT}
              onNavigate={setView}
              onReturn={goMain}
              isPlatAdmin={isPlatAdmin}
            />
          )}
          {view === 'admin' && (
            <PlatinumAdmin isPlatformAdmin={isPlatformAdmin} />
          )}
          {view === 'training' && (
            <PlatinumTraining user={user} />
          )}
          {SECTION_META[view] && (
            <PlatinumComingSoon title={SECTION_META[view].title} desc={SECTION_META[view].desc} />
          )}
        </main>
      </div>
    </div>
  );
};

const LEFT_BLOCKS = [
  { key: 'training',    icon: GraduationCap, label: 'Training',                   desc: 'Deepen knowledge. Expand capacity. Serve with excellence.' },
  { key: 'livestreams', icon: Radio,         label: 'Live Streams & Activations', desc: 'Join live events, ceremonies, and activations.' },
  { key: 'archive',     icon: ArchiveIcon,   label: 'Archive of Collective Work', desc: 'Explore past sessions, resources, and collaborative projects.' },
];
const RIGHT_BLOCKS = [
  { key: 'community',   icon: Users,    label: 'Community Engagement',   desc: 'Connect, collaborate, and grow together in the field.' },
  { key: 'cocreation',  icon: Sparkles, label: 'Co\u2011Creation Chamber', desc: 'Collaborate on projects and bring visions to life.' },
  { key: 'return',      icon: LogOut,   label: 'Return to Main Platform', desc: 'Back to the main 11/11 platform home.' },
];

const PILLARS = [
  { icon: Sprout, title: 'We Hold the Field', desc: 'We stand as stewards of coherence — holding presence, love, and purpose so the collective field remains clear and aligned.', tags: 'PRESENCE · LOVE · STEWARDSHIP', tagIcon: Heart },
  { icon: Heart,  title: 'Collective Impact', desc: 'Through conscious collaboration, we transform insight into action. Every contribution ripples outward to create real, measurable change.', tags: 'COLLABORATE · ACT · TRANSFORM', tagIcon: Send },
  { icon: Users,  title: 'Global Community',  desc: 'Join a living network of guides, healers, and visionaries. Together we embody unity across cultures, disciplines, and dimensions.', tags: 'UNITY · DIVERSITY · BELONGING', tagIcon: Globe },
  { icon: Atom,   title: 'Unified Vision',    desc: 'One field, one intention, many expressions — we honor diversity as the living proof of oneness in motion.', tags: 'ONE FIELD · ONE INTENTION', tagIcon: Sparkles },
];

const NavCard = ({ block, onClick, testid }) => {
  const Icon = block.icon;
  return (
    <button className="pp-card" onClick={onClick} data-testid={testid}>
      <span className="pp-card-icon"><Icon className="w-6 h-6" /></span>
      <span className="pp-card-body">
        <span className="pp-card-title">{block.label}</span>
        <span className="pp-card-desc">{block.desc}</span>
      </span>
      <ChevronRight className="pp-card-chev w-5 h-5" />
    </button>
  );
};

const PlatinumHome = ({ statement, onNavigate, onReturn, isPlatAdmin }) => {
  const go = (key) => (key === 'return' ? onReturn() : onNavigate(key));
  const rightBlocks = isPlatAdmin
    ? [
        ...RIGHT_BLOCKS.slice(0, -1),
        { key: 'admin', icon: Shield, label: 'Platinum+ Admin Area', desc: 'Manage Platinum+ admins, the intro video, and this space.' },
        RIGHT_BLOCKS[RIGHT_BLOCKS.length - 1],
      ]
    : RIGHT_BLOCKS;
  return (
    <div className="pp-home" data-testid="platinum-home">
      <div className="pp-home-grid">
        <div className="pp-col">
          {LEFT_BLOCKS.map((b) => <NavCard key={b.key} block={b} onClick={() => go(b.key)} testid={`platinum-block-${b.key}`} />)}
        </div>

        <div className="pp-statement" data-testid="platinum-statement">
          <ElevenLogo className="pp-statement-logo" />
          <div className="pp-divider"><span /></div>
          <p>{statement}</p>
          <svg className="pp-sunburst" viewBox="0 0 120 24" aria-hidden="true">
            {Array.from({ length: 11 }).map((_, i) => {
              const a = (Math.PI * (i / 10));
              const x2 = 60 + Math.cos(Math.PI - a) * 52;
              const y2 = 22 - Math.sin(a) * 18;
              return <line key={i} x1="60" y1="22" x2={x2} y2={y2} stroke="#C79BBE" strokeWidth="1" />;
            })}
          </svg>
        </div>

        <div className="pp-col">
          {rightBlocks.map((b) => <NavCard key={b.key} block={b} onClick={() => go(b.key)} testid={`platinum-block-${b.key}`} />)}
        </div>
      </div>

      {/* Lower pillar block — hidden on mobile */}
      <div className="pp-pillars" data-testid="platinum-pillars">
        {PILLARS.map((p) => {
          const Icon = p.icon; const TagIcon = p.tagIcon;
          return (
            <div key={p.title} className="pp-pillar" data-testid={`platinum-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <span className="pp-pillar-icon"><Icon className="w-6 h-6" /></span>
              <h4>{p.title}</h4>
              <span className="pp-pillar-diamond" />
              <p>{p.desc}</p>
              <div className="pp-pillar-foot">
                <span className="pp-pillar-tags">{p.tags}</span>
                <TagIcon className="w-4 h-4" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <footer className="pp-footer" data-testid="platinum-footer">
        <div className="pp-footer-brand">
          <span className="pp-footer-badge"><ElevenLogo mark className="pp-footer-logo" /></span>
          <div className="pp-footer-brand-txt">
            <strong>Collective Connection</strong>
            <span>One Field, Many Expressions.</span>
          </div>
        </div>
        <div className="pp-footer-center">
          <Sparkles className="w-4 h-4" /> Collective Connection · We Co‑Create Consciously.
        </div>
        <div className="pp-footer-links">
          <a href="/dashboard/messages" data-testid="platinum-footer-support"><HelpCircle className="w-4 h-4" /> Support</a>
          <a href="/dashboard/resources" data-testid="platinum-footer-resources"><BookOpen className="w-4 h-4" /> Resources</a>
          <a href="/community-guidelines" target="_blank" rel="noopener noreferrer" data-testid="platinum-footer-guidelines"><ShieldCheck className="w-4 h-4" /> Guidelines</a>
        </div>
      </footer>
    </div>
  );
};

const THEME_CSS = `
  :root {
    --pp-aubergine: #7F0D6C;
    --pp-aubergine-deep: #5C0A4E;
    --pp-cream: #FFFDD0;
    --pp-platinum: #E5E4E2;
    --pp-ink: #4a2340;
    --pp-muted: #7c5f74;
  }
  .pp-root { position: fixed; inset: 0; z-index: 55; overflow: hidden; display: flex; flex-direction: column;
    font-family: 'Cormorant Garamond', serif; color: var(--pp-ink); background: var(--pp-platinum); }
  .pp-center { align-items: center; justify-content: center; }
  .pp-bg { position: fixed; inset: 0; z-index: 0;
    background-color: #EBE6EC;
    background-size: cover; background-position: center top; background-repeat: no-repeat; }
  .pp-veil { position: fixed; inset: 0; z-index: 1; pointer-events: none;
    background: radial-gradient(circle at 50% 30%, rgba(255,255,255,0.10), transparent 70%); }
  .pp-stars { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: .4;
    background-image:
      radial-gradient(1.5px 1.5px at 18% 30%, rgba(127,13,108,.4), transparent),
      radial-gradient(1px 1px at 72% 22%, rgba(127,13,108,.3), transparent),
      radial-gradient(1.5px 1.5px at 42% 72%, rgba(127,13,108,.3), transparent),
      radial-gradient(1px 1px at 86% 64%, rgba(127,13,108,.28), transparent);
    background-size: 100% 100%; animation: ppTwinkle 8s ease-in-out infinite; }
  @keyframes ppTwinkle { 0%,100% { opacity: .25; } 50% { opacity: .45; } }

  /* Top ribbon */
  .pp-topbar { position: relative; z-index: 4; flex-shrink: 0; height: 78px; overflow: hidden;
    display: flex; align-items: center; justify-content: space-between; padding: 0 1.4rem;
    background: linear-gradient(100deg, var(--pp-aubergine-deep) 0%, var(--pp-aubergine) 100%);
    box-shadow: 0 8px 28px rgba(92,10,78,0.28); }
  .pp-topbar-brand { position: relative; z-index: 1; display: flex; align-items: center; gap: .8rem; color: var(--pp-cream); min-width: 0; }
  .pp-topbar-badge { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
    width: 54px; height: 54px; border-radius: 50%; background: var(--pp-cream); box-shadow: 0 3px 10px rgba(0,0,0,0.22); }
  .pp-topbar-logo { width: 40px; height: auto; }
  .pp-topbar-titles { display: flex; flex-direction: column; line-height: 1.05; }
  .pp-topbar-word { font-size: 1.45rem; font-weight: 700; letter-spacing: .01em; white-space: nowrap; }
  .pp-topbar-sub { font-size: .95rem; font-style: italic; color: rgba(255,253,240,0.82); }
  .pp-topbar-actions { position: relative; z-index: 1; display: flex; align-items: center; gap: .5rem; }
  .pp-member-badge { display: inline-flex; align-items: center; gap: .4rem; font-size: .95rem; font-weight: 600;
    color: var(--pp-aubergine); background: var(--pp-cream); border-radius: 999px; padding: .35rem .95rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.14); }
  .pp-member-badge svg { color: var(--pp-aubergine); }
  .pp-user-trigger { border: 2px solid var(--pp-cream); border-radius: 999px; padding: 1px;
    transition: transform .2s ease, box-shadow .2s ease; }
  .pp-user-trigger:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.25); }

  .pp-body { position: relative; z-index: 2; flex: 1; display: flex; min-height: 0; }

  /* Left navigation — Cream (sub-pages only) */
  .pp-ribbon { position: relative; z-index: 3; width: 250px; flex-shrink: 0;
    display: flex; flex-direction: column; padding: 1.4rem 1rem;
    background: var(--pp-cream);
    border-right: 1px solid rgba(127,13,108,0.22); box-shadow: 4px 0 30px rgba(75,9,66,0.08); }
  .pp-brand { display: flex; align-items: center; gap: .6rem; font-size: 1.4rem; font-weight: 700;
    color: var(--pp-aubergine); padding: 0 .4rem 1.1rem; letter-spacing: .01em; }
  .pp-crown { position: relative; display: inline-flex; color: var(--pp-aubergine); }
  .pp-plus { position: absolute; top: -6px; right: -8px; font-size: .7rem; font-weight: 800; color: var(--pp-aubergine); }
  .pp-preview-badge { font-size: .7rem; letter-spacing: .08em; text-transform: uppercase; color: var(--pp-aubergine);
    background: rgba(127,13,108,0.10); border: 1px solid rgba(127,13,108,0.4); border-radius: 999px;
    padding: .15rem .55rem; margin: 0 .4rem .75rem; text-align: center; }
  .pp-nav { display: flex; flex-direction: column; gap: .25rem; flex: 1; }
  .pp-nav-item { display: flex; align-items: center; gap: .6rem; text-align: left;
    padding: .6rem .7rem; border-radius: 10px; color: var(--pp-ink); font-size: 1.05rem;
    transition: background .25s ease, color .25s ease; }
  .pp-nav-item svg { color: var(--pp-aubergine); flex-shrink: 0; }
  .pp-nav-item:hover { background: rgba(127,13,108,0.08); color: var(--pp-aubergine); }
  .pp-nav-item.active { background: rgba(127,13,108,0.12); color: var(--pp-aubergine); font-weight: 600;
    box-shadow: inset 3px 0 0 var(--pp-aubergine); }
  .pp-return { display: flex; align-items: center; gap: .6rem; margin-top: 1rem; padding: .65rem .7rem;
    border-radius: 10px; color: var(--pp-aubergine); font-size: 1rem; border: 1px solid rgba(127,13,108,0.4);
    transition: all .25s ease; }
  .pp-return svg { color: var(--pp-aubergine); }
  .pp-return:hover { background: rgba(127,13,108,0.10); }

  .pp-content { position: relative; z-index: 2; flex: 1; overflow-y: auto; padding: 2rem 2.25rem 2.5rem; }

  .pp-home { display: flex; flex-direction: column; gap: 1.75rem; max-width: 1200px; margin: 0 auto; }
  .pp-home-grid { display: grid; gap: 1.5rem; grid-template-columns: 1fr 1.32fr 1fr; align-items: stretch; }
  .pp-col { display: flex; flex-direction: column; gap: 1.5rem; }

  /* Horizontal nav cards */
  .pp-card { display: flex; align-items: center; gap: 1rem; text-align: left; width: 100%;
    padding: 1.15rem 1.25rem; border-radius: 18px; cursor: pointer; flex: 1;
    background: var(--pp-cream); border: 1px solid rgba(127,13,108,0.14);
    box-shadow: 0 8px 26px rgba(120,80,110,0.10);
    transition: transform .28s ease, box-shadow .28s ease, border-color .28s ease; }
  .pp-card:hover { transform: translateY(-4px); border-color: rgba(127,13,108,0.4);
    box-shadow: 0 16px 38px rgba(127,13,108,0.18); }
  .pp-card-icon { flex-shrink: 0; width: 52px; height: 52px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    color: var(--pp-aubergine); background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.85), var(--pp-platinum));
    border: 1px solid rgba(127,13,108,0.4); }
  .pp-card-body { display: flex; flex-direction: column; gap: .2rem; flex: 1; min-width: 0; }
  .pp-card-title { font-size: 1.2rem; font-weight: 700; color: var(--pp-aubergine); line-height: 1.15; }
  .pp-card-desc { font-size: .95rem; color: var(--pp-muted); line-height: 1.3; }
  .pp-card-chev { flex-shrink: 0; color: rgba(127,13,108,0.5); transition: transform .25s ease; }
  .pp-card:hover .pp-card-chev { transform: translateX(3px); color: var(--pp-aubergine); }

  /* Center statement */
  .pp-statement { display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 2.25rem 2rem; border-radius: 22px;
    background: var(--pp-cream);
    border: 1px solid rgba(127,13,108,0.28);
    box-shadow: 0 16px 46px rgba(92,10,78,0.14), inset 0 0 40px rgba(255,252,235,0.5); }
  .pp-statement-logo { width: 150px; height: auto; }
  .pp-divider { width: 62%; height: 1px; margin: 1.1rem 0 1.25rem; position: relative;
    background: linear-gradient(90deg, transparent, rgba(127,13,108,0.35), transparent); }
  .pp-divider span { position: absolute; left: 50%; top: 50%; width: 8px; height: 8px; transform: translate(-50%,-50%) rotate(45deg);
    background: var(--pp-aubergine); border-radius: 1px; }
  .pp-statement p { font-size: 1.42rem; line-height: 1.55; color: var(--pp-ink); font-style: italic; max-width: 30rem; }
  .pp-sunburst { width: 120px; height: 24px; margin-top: 1.25rem; opacity: .8; }

  /* Lower pillar block */
  .pp-pillars { display: grid; gap: 1.25rem; grid-template-columns: repeat(4, 1fr); }
  .pp-pillar { display: flex; flex-direction: column; align-items: center; text-align: center; gap: .55rem;
    padding: 1.5rem 1.25rem 1rem; border-radius: 18px; background: var(--pp-cream);
    border: 1px solid rgba(127,13,108,0.14); box-shadow: 0 8px 26px rgba(120,80,110,0.10); }
  .pp-pillar-icon { width: 52px; height: 52px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    color: var(--pp-aubergine); background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.85), var(--pp-platinum));
    border: 1px solid rgba(127,13,108,0.4); }
  .pp-pillar h4 { font-size: 1.28rem; font-weight: 700; color: var(--pp-aubergine); }
  .pp-pillar-diamond { width: 7px; height: 7px; transform: rotate(45deg); background: rgba(127,13,108,0.55); border-radius: 1px; }
  .pp-pillar p { font-size: .98rem; line-height: 1.45; color: var(--pp-muted); flex: 1; }
  .pp-pillar-foot { width: 100%; margin-top: .75rem; padding-top: .7rem; border-top: 1px solid rgba(127,13,108,0.15);
    display: flex; align-items: center; justify-content: space-between; gap: .5rem; }
  .pp-pillar-tags { font-size: .68rem; letter-spacing: .06em; font-weight: 600; color: var(--pp-aubergine); text-transform: uppercase; }
  .pp-pillar-foot svg { color: var(--pp-aubergine); flex-shrink: 0; }

  /* Footer */
  .pp-footer { display: flex; align-items: center; justify-content: space-between; gap: 1.5rem; flex-wrap: wrap;
    margin-top: .5rem; padding: 1.25rem 1.75rem; border-radius: 18px; color: var(--pp-cream);
    background: linear-gradient(100deg, var(--pp-aubergine-deep), var(--pp-aubergine)); box-shadow: 0 12px 34px rgba(75,9,66,0.28); }
  .pp-footer-brand { display: flex; align-items: center; gap: .8rem; }
  .pp-footer-badge { display: inline-flex; align-items: center; justify-content: center; width: 46px; height: 46px;
    border-radius: 50%; background: var(--pp-cream); flex-shrink: 0; }
  .pp-footer-logo { width: 34px; height: auto; }
  .pp-footer-brand-txt { display: flex; flex-direction: column; line-height: 1.1; }
  .pp-footer-brand-txt strong { font-size: 1.2rem; font-weight: 700; }
  .pp-footer-brand-txt span { font-size: .9rem; color: rgba(255,253,240,0.78); }
  .pp-footer-center { display: inline-flex; align-items: center; gap: .5rem; font-style: italic; font-size: 1rem;
    color: rgba(255,253,240,0.9); text-align: center; }
  .pp-footer-center svg { color: #F6C89B; }
  .pp-footer-links { display: flex; align-items: center; gap: 1.4rem; }
  .pp-footer-links a { display: inline-flex; align-items: center; gap: .4rem; font-size: 1rem;
    color: rgba(255,253,240,0.88); transition: color .2s ease; }
  .pp-footer-links a:hover { color: #fff; }

  .pp-intro { position: fixed; inset: 0; z-index: 60; background: #0a0a0a; display: flex; align-items: center; justify-content: center; }
  .pp-unmute { position: absolute; bottom: 24px; right: 24px; display: inline-flex; align-items: center; gap: .4rem;
    background: rgba(255,255,255,0.12); color: #fff; border: 1px solid rgba(255,255,255,0.4);
    border-radius: 999px; padding: .5rem 1rem; font-size: .9rem; backdrop-filter: blur(6px); }

  .pp-locked { position: relative; z-index: 2; max-width: 460px; text-align: center; padding: 2.5rem;
    background: var(--pp-cream); border: 1px solid rgba(127,13,108,0.4); border-radius: 20px; }

  /* ── Training ─────────────────────────────────────────────────────────── */
  .pp-train { max-width: 940px; margin: 0 auto; }
  .pp-train-wait { display: flex; align-items: center; justify-content: center; padding: 5rem; color: var(--pp-aubergine); }
  .pp-train-head { margin-bottom: 1.75rem; }
  .pp-train-head h1 { display: flex; align-items: center; gap: .6rem; font-size: 2.2rem; font-weight: 700; color: var(--pp-aubergine); }
  .pp-train-head h1 svg { color: var(--pp-aubergine); }
  .pp-train-head p { font-size: 1.05rem; font-style: italic; color: var(--pp-muted); margin-top: .35rem; }
  .pp-train-empty { text-align: center; padding: 3.5rem 2rem; border-radius: 20px;
    background: var(--pp-cream); border: 1px solid rgba(127,13,108,0.22); }
  .pp-train-empty svg { color: var(--pp-aubergine); margin: 0 auto .75rem; opacity: .75; }
  .pp-train-empty h3 { font-size: 1.5rem; font-weight: 700; color: var(--pp-aubergine); margin-bottom: .4rem; }
  .pp-train-empty p { color: var(--pp-muted); font-size: 1.02rem; max-width: 30rem; margin: 0 auto; }
  .pp-train-list { display: flex; flex-direction: column; gap: 1rem; }
  .pp-train-card { display: flex; align-items: center; gap: 1rem; width: 100%; text-align: left;
    padding: 1.15rem 1.25rem; border-radius: 18px; background: var(--pp-cream);
    border: 1px solid rgba(127,13,108,0.14); box-shadow: 0 8px 26px rgba(120,80,110,0.10);
    transition: transform .28s ease, box-shadow .28s ease, border-color .28s ease; }
  .pp-train-card:not(:disabled):hover { transform: translateY(-3px); border-color: rgba(127,13,108,0.4);
    box-shadow: 0 16px 38px rgba(127,13,108,0.18); }
  .pp-train-card:disabled { opacity: .62; cursor: not-allowed; }
  .pp-train-icon { flex-shrink: 0; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    color: var(--pp-aubergine); background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.85), var(--pp-platinum));
    border: 1px solid rgba(127,13,108,0.4); }
  .pp-train-body { display: flex; flex-direction: column; gap: .18rem; flex: 1; min-width: 0; }
  .pp-train-title { font-size: 1.2rem; font-weight: 700; color: var(--pp-aubergine); line-height: 1.15; }
  .pp-train-desc { font-size: .95rem; color: var(--pp-muted); line-height: 1.35; }
  .pp-train-note { display: inline-flex; align-items: center; gap: .3rem; font-size: .8rem; color: var(--pp-muted); margin-top: .25rem; }
  .pp-train-note.pp-done { color: #2f7a41; }
  .pp-train-chev { flex-shrink: 0; color: rgba(127,13,108,0.5); }

  /* ── The one reader, wearing Platinum+ colours ────────────────────────────
     The content reader and the content admin forms are the main platform's own
     components. They are driven by shadcn's CSS variables, so re-pointing those
     variables here dresses the identical components in cream, gold and aubergine —
     one reader to maintain, not two. The handful of hard-coded dark-theme colours
     in those files are corrected underneath. */
  .pp-skin {
    --background: 50 60% 97%;
    --foreground: 315 37% 22%;
    --card: 48 60% 98%;
    --card-foreground: 315 37% 22%;
    --popover: 48 60% 98%;
    --popover-foreground: 315 37% 22%;
    --primary: 309 82% 28%;
    --primary-foreground: 60 100% 91%;
    --secondary: 309 25% 93%;
    --secondary-foreground: 309 82% 28%;
    --muted: 309 18% 94%;
    --muted-foreground: 315 15% 44%;
    --accent: 309 30% 93%;
    --accent-foreground: 309 82% 28%;
    --destructive: 0 62% 45%;
    --destructive-foreground: 60 100% 96%;
    --border: 309 30% 84%;
    --input: 309 30% 80%;
    --ring: 309 82% 28%;
    color: var(--pp-ink);
  }
  .pp-skin .text-white { color: var(--pp-aubergine-deep) !important; }
  .pp-skin .text-gray-300, .pp-skin .text-slate-300 { color: var(--pp-ink) !important; }
  .pp-skin .text-gray-400, .pp-skin .text-gray-500 { color: var(--pp-muted) !important; }
  .pp-skin .text-gray-600, .pp-skin .text-gray-700, .pp-skin .text-gray-900 { color: var(--pp-ink) !important; }
  .pp-skin .bg-white { background-color: #FFFDF6 !important; }
  .pp-skin .bg-gray-50 { background-color: rgba(127,13,108,0.05) !important; }
  .pp-skin .hover\\:bg-gray-50:hover { background-color: rgba(127,13,108,0.06) !important; }
  .pp-skin .bg-slate-950\\/70, .pp-skin .bg-white\\/5, .pp-skin .bg-white\\/10 {
    background-color: rgba(255,253,246,0.8) !important; }
  .pp-skin .border-white\\/10, .pp-skin .border-white\\/25 { border-color: rgba(127,13,108,0.22) !important; }
  .pp-skin .text-purple-700 { color: var(--pp-aubergine) !important; }
  .pp-skin .bg-purple-100 { background-color: rgba(127,13,108,0.10) !important; }
  .pp-skin .btn-primary { background: linear-gradient(135deg, var(--pp-aubergine), var(--pp-aubergine-deep));
    color: var(--pp-cream); }
  /* The main platform's cyan→purple action buttons re-dressed for the sanctuary */
  .pp-skin .bg-gradient-to-r { background-image: linear-gradient(90deg, var(--pp-aubergine), var(--pp-aubergine-deep)) !important;
    color: var(--pp-cream) !important; }
  .pp-reader { max-width: 100%; }
  .pp-reader h1 { color: var(--pp-aubergine) !important; }
  .pp-locked h2 { font-size: 2rem; font-weight: 700; color: var(--pp-aubergine); margin-bottom: .5rem; }
  .pp-locked p { color: var(--pp-ink); font-size: 1.1rem; line-height: 1.5; margin-bottom: 1.5rem; }
  .pp-btn-solid { background: linear-gradient(135deg, var(--pp-aubergine), var(--pp-aubergine-deep)); color: var(--pp-cream);
    font-weight: 600; padding: .7rem 1.5rem; border-radius: 999px; font-size: 1rem; }

  @media (max-width: 980px) {
    .pp-home-grid { grid-template-columns: 1fr; }
    .pp-statement { order: -1; }
    .pp-pillars { display: none; }
    .pp-footer { flex-direction: column; text-align: center; }
    .pp-footer-links { flex-wrap: wrap; justify-content: center; }
  }
  @media (max-width: 900px) {
    .pp-topbar-sub { display: none; }
    .pp-topbar-word { font-size: 1.2rem; }
    .pp-ribbon { position: fixed; bottom: 0; left: 0; right: 0; top: auto; width: 100%; flex-direction: row;
      overflow-x: auto; padding: .5rem; z-index: 6; }
    .pp-brand, .pp-preview-badge { display: none; }
    .pp-nav { flex-direction: row; }
    .pp-nav-item span, .pp-return span { display: none; }
    .pp-return { margin-top: 0; }
    .pp-content { padding: 1.25rem 1.25rem 5rem; }
  }
`;

export default PlatinumPlus;
